import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useStore } from '../store'
import { colors, fonts } from '../components/UI'
import { getPayCycle, cycleRangeLabel } from '../lib/payday'
import Dashboard from './Dashboard'
import BudgetPage from './BudgetPage'
import BillsPage from './BillsPage'
import GoalsPage from './GoalsPage'
import IncomePage from './IncomePage'
import InvestmentsPage from './InvestmentsPage'
import InsightsPage from './InsightsPage'
import LearnPage from './LearnPage'
import SettingsPage from './SettingsPage'
import ImportPage from './ImportPage'
import MorePage from './MorePage'
import NotificationsPage from './NotificationsPage'
import BlueprintPage from './BlueprintPage'

// ── Mobile bottom nav ──────────────────────────────────────────────────────
const MOBILE_NAV = [
  { id: 'dashboard', label: 'Home',   emoji: '🏠' },
  { id: 'budget',    label: 'Budget', emoji: '💼' },
  { id: 'bills',     label: 'Bills',  emoji: '🧾' },
  { id: 'goals',     label: 'Goals',  emoji: '🎯' },
  { id: 'more',      label: 'More',   emoji: '···' },
]

// ── Desktop sidebar nav ────────────────────────────────────────────────────
const SIDEBAR_NAV = [
  { id: 'dashboard', label: 'Dashboard', emoji: '🏠' },
  { id: 'budget',    label: 'Budget',    emoji: '💼' },
  { id: 'bills',     label: 'Bills',     emoji: '🧾' },
  { id: 'goals',     label: 'Goals',     emoji: '🎯' },
  { id: 'more',      label: 'More',      emoji: '···' },
]
const SIDEBAR_SECONDARY = []

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 900)
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 900)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [])
  return mobile
}

function PageContent({ page, onNavigate, defaultTab, basicMode }) {
  if (page === 'dashboard')   return <Dashboard onNavigate={onNavigate} basicMode={basicMode} />
  if (page === 'budget')      return <BudgetPage onNavigate={onNavigate} />
  if (page === 'bills')       return <BillsPage onNavigate={onNavigate} />
  if (page === 'goals')       return <GoalsPage onNavigate={onNavigate} basicMode={basicMode} />
  if (page === 'income')      return <IncomePage />
  if (page === 'investments') return <InvestmentsPage />
  if (page === 'insights')    return <InsightsPage />
  if (page === 'learn')       return <LearnPage defaultTab={defaultTab} />
  if (page === 'appguide')    return <LearnPage defaultTab="appguide" />
  if (page === 'settings')    return <SettingsPage onNavigate={onNavigate} />
  if (page === 'import')      return <ImportPage onNavigate={onNavigate} defaultTab={defaultTab} />
  if (page === 'more')        return <MorePage onNavigate={onNavigate} />
  if (page === 'notifications') return <NotificationsPage />
  if (page === 'blueprint')    return <BlueprintPage />
  return <Dashboard onNavigate={onNavigate} basicMode={basicMode} />
}

export default function AppShell() {
  const { user, profile, signOut } = useAuth()
  const [page, setPage] = useState('dashboard')
  const [defaultTab, setDefaultTab] = useState(null)
  const isMobile = useIsMobile()
  const basicMode = profile?.basic_mode === true

  const BASIC_NAV = [
    { id: 'dashboard', label: 'Home',   emoji: '🏠' },
    { id: 'budget',    label: 'Budget', emoji: '💼' },
    { id: 'bills',     label: 'Bills',  emoji: '🧾' },
    { id: 'more',      label: 'More',   emoji: '···' },
  ]

  const activeNav = basicMode ? BASIC_NAV : MOBILE_NAV
  const activeSidebar = basicMode ? BASIC_NAV : SIDEBAR_NAV

  useEffect(() => {
    if (user?.id) useStore.getState().init(user.id)
    else useStore.getState().teardown()
  }, [user?.id])

  useEffect(() => {
    const h = e => { setPage(e.detail); setDefaultTab(null) }
    window.addEventListener('navigate', h)
    return () => window.removeEventListener('navigate', h)
  }, [])

  const payDay = profile?.pay_day
  const shiftWeekends = profile?.pay_day_shift_weekends !== false
  const currentCycle = useMemo(() => getPayCycle(payDay, new Date(), shiftWeekends), [payDay, shiftWeekends])

  const initials = (profile?.full_name || user?.email || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'User'
  const firstName = displayName.split(' ')[0]

  function navigate(id, tab = null) { setPage(id); setDefaultTab(tab); window.scrollTo(0, 0) }

  const isMorePage = !activeNav.some(n => n.id === page) || page === 'more'
  const activeNavId = activeNav.some(n => n.id === page) ? page : 'more'

  // ── MOBILE ─────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', height: '100dvh', background: colors.canvas, overflow: 'hidden' }}>
        <main style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '72px', background: colors.leaf, backgroundAttachment: 'local' }}>
          <PageContent page={page} onNavigate={navigate} defaultTab={defaultTab} basicMode={basicMode} />
        </main>

        {/* Bottom nav */}
        <nav style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          height: `calc(60px + env(safe-area-inset-bottom))`,
          paddingBottom: 'env(safe-area-inset-bottom)',
          background: colors.navy,
          borderTop: `1px solid ${colors.borderGold}`,
          display: 'flex', zIndex: 50,
        }}>
          {activeNav.map(item => {
            const active = activeNavId === item.id
            return (
              <button
                key={item.id}
                data-walkthrough={item.id === 'budget' ? 'nav-budget' : item.id === 'bills' ? 'nav-bills' : item.id === 'goals' ? 'nav-goals' : undefined}
                onClick={() => navigate(item.id)}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', gap: '3px', border: 'none', background: 'transparent',
                  cursor: 'pointer', padding: '8px 4px',
                  borderTop: `2px solid ${active ? colors.gold : 'transparent'}`,
                }}
              >
                <span style={{ fontSize: item.id === 'more' ? '14px' : '21px', lineHeight: 1, color: active ? colors.gold : `rgba(${colors.textRgb},0.62)` }}>
                  {item.emoji}
                </span>
                <span style={{ fontSize: '14px', fontFamily: fonts.body, fontWeight: 700, letterSpacing: '0.01em', color: active ? colors.gold : `rgba(${colors.textRgb},0.64)` }}>
                  {item.label}
                </span>
              </button>
            )
          })}
        </nav>
      </div>
    )
  }

  // ── DESKTOP ─────────────────────────────────────────────────────────────
  const sidebarBg = colors.bg
  return (
    <div style={{ display: 'flex', height: '100vh', background: colors.canvas, overflow: 'hidden' }}>
      {/* Sidebar */}
      <aside style={{
        width: '220px', flexShrink: 0,
        background: sidebarBg,
        borderRight: `1px solid rgba(201,151,43,0.15)`,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Logo */}
        <div style={{ padding: '22px 20px 18px', borderBottom: `1px solid rgba(201,151,43,0.1)` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <div style={{ width: '2px', flexShrink: 0, alignSelf: 'stretch', background: colors.gold, borderRadius: '1px', boxShadow: `0 0 8px ${colors.glow}` }} />
            <div>
              <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 300, fontSize: '12px', letterSpacing: '0.32em', color: `rgba(${colors.textRgb},0.62)`, marginBottom: '1px' }}>THE</div>
              <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: '18px', letterSpacing: '0.03em', color: colors.text, lineHeight: 1 }}>INVESTING</div>
              <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 300, fontSize: '13px', letterSpacing: '0.16em', color: colors.gold, lineHeight: 1.3 }}>COUPLE</div>
              <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: '12px', letterSpacing: '0.18em', color: `rgba(${colors.textRgb},0.5)`, borderTop: '1px solid rgba(201,151,43,0.15)', paddingTop: '3px', marginTop: '4px' }}>TRACKER</div>
            </div>
          </div>
        </div>

        {/* Primary nav */}
        <nav style={{ flex: 1, padding: '12px 10px', overflowY: 'auto' }}>
          {activeSidebar.map(item => {
            const active = page === item.id
            return (
              <button key={item.id} data-walkthrough={item.id === 'budget' ? 'nav-budget' : item.id === 'bills' ? 'nav-bills' : item.id === 'goals' ? 'nav-goals' : undefined} onClick={() => navigate(item.id)} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 12px', borderRadius: '10px', cursor: 'pointer',
                width: '100%', textAlign: 'left', border: 'none', marginBottom: '2px',
                background: active ? colors.borderGold : 'transparent',
                color: active ? colors.gold : `rgba(${colors.textRgb},0.65)`,
                fontFamily: fonts.body, fontWeight: 600, fontSize: '14px',
                transition: 'all 0.15s',
              }}>
                <span style={{ fontSize: '14px', width: '22px', textAlign: 'center', lineHeight: 1 }}>{item.emoji}</span>
                {item.label}
              </button>
            )
          })}

          <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)', margin: '10px 4px' }} />

          {SIDEBAR_SECONDARY.map(item => {
            const active = page === item.id
            return (
              <button key={item.id} onClick={() => navigate(item.id)} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 12px', borderRadius: '10px', cursor: 'pointer',
                width: '100%', textAlign: 'left', border: 'none', marginBottom: '2px',
                background: active ? colors.borderGold : 'transparent',
                color: active ? colors.gold : `rgba(${colors.textRgb},0.80)`,
                fontFamily: fonts.body, fontWeight: 600, fontSize: '14px',
                transition: 'all 0.15s',
              }}>
                <span style={{ fontSize: '14px', width: '22px', textAlign: 'center', lineHeight: 1 }}>{item.emoji}</span>
                {item.label}
              </button>
            )
          })}
        </nav>

        {/* Cycle info */}
        {currentCycle && (
          <div style={{ margin: '0 10px 10px', background: 'rgba(201,151,43,0.06)', border: `1px solid rgba(201,151,43,0.2)`, borderRadius: '10px', padding: '12px' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.03em', color: colors.gold, marginBottom: '6px' }}>Current Period</div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '2px' }}>{cycleRangeLabel(currentCycle)}</div>
            <CycleDaysLeft cycle={currentCycle} />
          </div>
        )}

        {/* User */}
        <div style={{ padding: '10px', borderTop: `1px solid rgba(255,255,255,0.05)` }}>
          <div style={{ padding: '8px 10px', borderRadius: '10px', marginBottom: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(201,151,43,0.15)', border: `1px solid rgba(201,151,43,0.3)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: fonts.heading, fontWeight: 700, fontSize: '14px', color: colors.gold, flexShrink: 0 }}>{initials}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{firstName}</div>
                <div style={{ fontSize: '12px', color: `rgba(${colors.textRgb},0.64)`, marginTop: '1px' }}>Household</div>
              </div>
            </div>
            <button onClick={signOut} style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: `rgba(${colors.textRgb},0.72)`, cursor: 'pointer', fontSize: '12px', fontWeight: 600, fontFamily: fonts.body, padding: '7px 10px', letterSpacing: '0.04em' }}>Sign out</button>
          </div>
          <a href="https://theinvestingcouple.co.uk/starting-line/" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', textDecoration: 'none' }}>
            <span style={{ fontSize: '14px', color: 'rgba(201,151,43,0.4)', fontFamily: fonts.body, fontWeight: 600, letterSpacing: '0.02em' }}>The Starting Line ↗</span>
          </a>
        </div>
      </aside>

      <main style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: colors.leaf, backgroundAttachment: 'local' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto', paddingBottom: '40px' }}>
          <PageContent page={page} onNavigate={navigate} defaultTab={defaultTab} basicMode={basicMode} />
        </div>
      </main>
    </div>
  )
}

function CycleDaysLeft({ cycle }) {
  const today = new Date()
  const end = new Date(cycle.endIso)
  const diff = Math.max(0, Math.ceil((end - today) / 86400000))
  return (
    <div style={{ fontSize: '16px', color: diff <= 3 ? colors.gold : `rgba(${colors.textRgb},0.65)`, fontWeight: 600 }}>
      {diff === 0 ? 'Payday today' : `${diff} day${diff !== 1 ? 's' : ''} left`}
    </div>
  )
}
